import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Newspaper, Plus, Pin, Edit3, Trash2, Megaphone, Rocket, BookOpen, Save } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import { toast } from 'sonner';

const CATEGORY_OPTIONS = [
  { id: 'announcement', label: 'Announcement', icon: Megaphone, color: 'text-violet-600 bg-violet-50' },
  { id: 'release', label: 'Release Notes', icon: Rocket, color: 'text-emerald-600 bg-emerald-50' },
  { id: 'blog', label: 'Blog', icon: BookOpen, color: 'text-amber-600 bg-amber-50' },
];

const News = ({ user, setUser }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const isSuperAdmin = !!user?.is_super_admin;

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/news`);
      setItems(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing({
      title: '', content: '', excerpt: '', category: 'announcement',
      cover_image_url: '', is_published: true, is_pinned: false, broadcast_to_all: false,
    });
    setShowDialog(true);
  };

  const openEdit = (item) => {
    setEditing({ ...item });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!editing.title || !editing.content) {
      toast.error('Title and content are required');
      return;
    }
    try {
      if (editing.id) {
        const { id, broadcast_to_all, author_id, author_name, created_at, updated_at, workspace_id, ...payload } = editing;
        await axios.patch(`${API_URL}/news/${id}`, payload);
        toast.success('Updated');
      } else {
        await axios.post(`${API_URL}/news`, editing);
        toast.success('Published');
      }
      setShowDialog(false);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API_URL}/news/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8 max-w-7xl mx-auto" data-testid="news-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-violet-600">
                <Newspaper className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>News & Announcements</h1>
            </div>
            <p className="text-sm text-zinc-600">Post product updates, release notes, and announcements to your portal.</p>
          </div>
          <Button onClick={openCreate} className="bg-violet-600 hover:bg-violet-700" data-testid="new-news-button">
            <Plus className="h-4 w-4 mr-2" />New Post
          </Button>
        </div>

        {loading ? <p className="text-zinc-500">Loading…</p> : items.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-zinc-200 rounded-2xl bg-zinc-50/50">
            <Newspaper className="h-12 w-12 mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500 mb-4">No posts yet</p>
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Write your first announcement</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(item => {
              const catOpt = CATEGORY_OPTIONS.find(c => c.id === item.category) || CATEGORY_OPTIONS[0];
              const Icon = catOpt.icon;
              return (
                <div key={item.id} className="bg-white border border-zinc-200 rounded-2xl p-5 hover:shadow-sm transition-shadow" data-testid={`news-item-${item.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${catOpt.color}`}>
                          <Icon className="h-3 w-3" />{catOpt.label}
                        </span>
                        {item.is_pinned && <Pin className="h-3.5 w-3.5 text-amber-500" />}
                        {!item.is_published && <span className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-500 text-xs">Draft</span>}
                        {!item.workspace_id && <span className="px-2 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 text-xs">Broadcast</span>}
                      </div>
                      <h3 className="text-lg font-bold text-zinc-900">{item.title}</h3>
                      {item.excerpt && <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{item.excerpt}</p>}
                      <div className="text-xs text-zinc-400 mt-2">by {item.author_name} · {new Date(item.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(item)} className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-600" data-testid={`edit-news-${item.id}`}><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing?.id ? 'Edit Post' : 'New Post'}</DialogTitle>
              <DialogDescription>Compose a new announcement, release note, or blog post.</DialogDescription>
            </DialogHeader>
            {editing && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Title</Label>
                  <Input value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} className="mt-1.5" data-testid="news-title-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {CATEGORY_OPTIONS.map(c => {
                      const I = c.icon;
                      return (
                        <button
                          key={c.id}
                          onClick={() => setEditing({...editing, category: c.id})}
                          className={`px-3 py-2.5 rounded-lg border-2 text-sm flex items-center gap-2 ${editing.category === c.id ? 'border-violet-400 bg-violet-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                        >
                          <I className="h-4 w-4" />{c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Excerpt (optional)</Label>
                  <Input value={editing.excerpt || ''} onChange={e => setEditing({...editing, excerpt: e.target.value})} className="mt-1.5" placeholder="One-line summary" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Content</Label>
                  <div className="mt-1.5" data-testid="news-content-input">
                    <RichTextEditor
                      value={editing.content}
                      onChange={(html) => setEditing({ ...editing, content: html })}
                      placeholder="Write your announcement…"
                      minHeight={260}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Cover Image URL (optional)</Label>
                  <Input value={editing.cover_image_url || ''} onChange={e => setEditing({...editing, cover_image_url: e.target.value})} className="mt-1.5" placeholder="https://…" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-200">
                  <span className="text-sm">Pin to top</span>
                  <Switch checked={!!editing.is_pinned} onCheckedChange={v => setEditing({...editing, is_pinned: v})} />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-200">
                  <span className="text-sm">Published</span>
                  <Switch checked={!!editing.is_published} onCheckedChange={v => setEditing({...editing, is_published: v})} />
                </div>
                {isSuperAdmin && !editing.id && (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-fuchsia-200 bg-fuchsia-50">
                    <div>
                      <div className="text-sm font-semibold text-fuchsia-900">Broadcast to all workspaces</div>
                      <div className="text-xs text-fuchsia-700">Super admin only — all workspaces will see this post</div>
                    </div>
                    <Switch checked={!!editing.broadcast_to_all} onCheckedChange={v => setEditing({...editing, broadcast_to_all: v})} />
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                  <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700" data-testid="news-save-button">
                    <Save className="h-4 w-4 mr-2" />Save
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default News;
